import React, { useEffect, useMemo, useState } from 'react';
import { usePapaParse } from 'react-papaparse';
import { Map } from '../map/Map';
import { MapFilter } from '../map/MapFilter';
import { PractitionerCard } from '../map/PractitionerCard';
import * as styles from './mapSection.module.scss';
import { useMapContext } from '../context/mapContext';
import { MapCardsMobile } from '../map/MapCardMobile';
import { SanityMapSection } from 'graphql-types';
import { usePageContext } from '../context/pageContext';
import { useJsApiLoader } from '@react-google-maps/api';
import { MapCardsDesktop } from '../map/MapCardsDesktop';
import useWindowSize from '../hooks/useWindowSize';
import { findTranslation } from '../../utils/findTranslation';

const libraries: any | undefined = ['places'];

export type Expert = {
  id: string;
  practiceName: string;
  phone: string;
  expertise: string[];
  address: string;
  city: string;
  zipcode: string;
  country: string;
  latitude: string;
  longitude: string;
  color: string;
  expertises: string;
};

export type Coords = {
  lat: number;
  lng: number;
};

interface Props {
  section: SanityMapSection;
}

export const MapSection: React.FC<Props> = ({ section }) => {
  const { selected, expertsList, setSelected } = useMapContext();
  const { lang, defaultLang } = usePageContext();
  const [cardsOpen, setCardsOpen] = useState<boolean>(false);
  const { width } = useWindowSize();

  if (!section.expertsList?.asset?.url) return null;

  const expertiseList: string[] = useMemo(
    () =>
      section.expertiseList
        ?.map((exp) => findTranslation(exp?._rawTitle, lang, defaultLang) ?? '')
        .filter((e) => e) ?? [],
    [],
  );

  const ref = React.createRef<HTMLDivElement>();

  const bounds = expertsList.map((item) => ({
    lng: parseFloat(item.longitude.replace(',', '.')),
    lat: parseFloat(item.latitude.replace(',', '.')),
  }));

  useEffect(() => {
    if (selected) {
      const index = expertsList.findIndex((e) => e.id === selected?.id);
      ref.current?.scrollTo({
        behavior: 'smooth',
        top: index * (196 + 18),
      });
    }
  }, [selected, expertsList]);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.GATSBY_GOOGLE_MAP_API_KEY as string,
    libraries: libraries,
    language: 'en',
  });

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <div className={styles.desktop}>
          <div className={styles.sectionTitle}>
            {section.isH1 ? (
              <h1>{findTranslation(section?._rawTitleMapSection, lang, defaultLang)}</h1>
            ) : (
              <h2>{findTranslation(section?._rawTitleMapSection, lang, defaultLang)}</h2>
            )}
          </div>
          <MapFilter
            expertiseList={expertiseList}
            results={expertsList}
            isLoaded={isLoaded}
            section={section}
          />
          {width >= 1064 && (
            <div
              className={styles.cards}
              ref={ref}
            >
              <MapCardsDesktop list={expertsList} />
            </div>
          )}
        </div>
        <div className={styles.map}>
          {width < 1064 && selected && (
            <div className={styles.mobileSelected}>
              <PractitionerCard
                data={selected}
                onClick={() => selected && width < 1064 && setSelected(undefined)}
                darkMode={true}
              />
            </div>
          )}
          <Map
            data={expertsList}
            boundsCoords={bounds}
            isLoaded={isLoaded}
          />
        </div>

        {width < 1064 && (
          <MapCardsMobile
            filteredResults={expertsList}
            section={section}
            open={cardsOpen}
            setOpen={setCardsOpen}
          />
        )}
      </div>
    </div>
  );
};
